import { createApi } from '@reduxjs/toolkit/query/react';
import { axiosBaseQuery } from 'config/axiosBaseQuery';
import { endpoints } from 'config/api';
import { PartnerLoginResponse } from 'features/auth/authTypes';
import {
  PartnerRelationship,
  PartnerProject,
  PartnerQuoteRequest,
  PartnerInvoice,
  PartnerBillingSummary,
  PartnerContacts,
  PartnerMessage,
  PartnerLoginPayload,
  AcceptInvitePayload,
  CreateQuoteRequestPayload,
  SwitchCompanyPayload,
  SwitchCompanyResponse,
  AdminPartnerRelationship,
  InvitePartnerPayload,
  InvitePartnerResponse,
  PartnerRelationshipStatus,
  PartnerLead,
  PartnerOwnTeamMember,
  CreateTeamMemberPayload,
  UpdateTeamMemberPayload,
} from './partnerTypes';
import { ChatMessagesResponse, ChatMessagesQueryParams } from 'features/projectChat/projectChatTypes';
import { CreateLeadDto, Lead } from 'features/leads/leadsTypes';

export const partnersApi = createApi({
  reducerPath: 'partnersApi',
  baseQuery: axiosBaseQuery(),
  tagTypes: [
    'PartnerProfile',
    'PartnerCompanies',
    'PartnerProjects',
    'PartnerQuotes',
    'PartnerInvoices',
    'PartnerContacts',
    'PartnerMessages',
    'PartnerBillingSummary',
    'AdminPartners',
    'AdminPartnerQuotes',
    'PartnerLeads',
    'PartnerTeamMembers',
  ],
  endpoints: builder => ({
    // ==================== AUTH ====================
    partnerLogin: builder.mutation<PartnerLoginResponse, PartnerLoginPayload>({
      query: data => ({
        url: endpoints.partners.login,
        method: 'POST',
        data,
      }),
    }),

    acceptPartnerInvite: builder.mutation<PartnerLoginResponse, AcceptInvitePayload>({
      query: data => ({
        url: endpoints.partners.acceptInvite,
        method: 'POST',
        data,
      }),
    }),

    // ==================== PROFILE ====================
    getPartnerProfile: builder.query<PartnerLoginResponse['partner'], void>({
      query: () => ({
        url: endpoints.partners.me,
        method: 'GET',
      }),
      providesTags: ['PartnerProfile'],
    }),

    updatePartnerProfile: builder.mutation<PartnerLoginResponse['partner'], Partial<PartnerLoginResponse['partner']>>({
      query: data => ({
        url: endpoints.partners.me,
        method: 'PUT',
        data,
      }),
      invalidatesTags: ['PartnerProfile'],
    }),

    // ==================== COMPANIES ====================
    getPartnerCompanies: builder.query<PartnerRelationship[], void>({
      query: () => ({
        url: endpoints.partners.companies,
        method: 'GET',
      }),
      providesTags: ['PartnerCompanies'],
    }),

    switchPartnerCompany: builder.mutation<SwitchCompanyResponse, SwitchCompanyPayload>({
      query: data => ({
        url: endpoints.partners.switchCompany,
        method: 'POST',
        data,
      }),
      invalidatesTags: [
        'PartnerProjects',
        'PartnerQuotes',
        'PartnerInvoices',
        'PartnerContacts',
        'PartnerMessages',
        'PartnerBillingSummary',
      ],
    }),

    // ==================== PROJECTS ====================
    getPartnerProjects: builder.query<PartnerProject[], void>({
      query: () => ({
        url: endpoints.partners.projects,
        method: 'GET',
      }),
      providesTags: ['PartnerProjects'],
    }),

    getPartnerProjectById: builder.query<PartnerProject, string>({
      query: projectId => ({
        url: endpoints.partners.projectById(projectId),
        method: 'GET',
      }),
      providesTags: (_r, _e, id) => [{ type: 'PartnerProjects', id }],
    }),

    getPartnerProjectChatMessages: builder.query<
      ChatMessagesResponse,
      { projectId: string; params?: ChatMessagesQueryParams }
    >({
      query: ({ projectId, params }) => {
        const queryParams = new URLSearchParams();
        if (params?.page) queryParams.append('page', params.page.toString());
        if (params?.limit) queryParams.append('limit', params.limit.toString());
        if (params?.before) queryParams.append('before', params.before);

        const queryString = queryParams.toString();
        return {
          url: `${endpoints.partners.projectChatMessages(projectId)}${queryString ? `?${queryString}` : ''}`,
          method: 'GET',
        };
      },
      providesTags: (_result, _error, { projectId }) => [
        { type: 'PartnerProjects', id: `chat-${projectId}` },
      ],
    }),

    // ==================== QUOTES ====================
    getPartnerQuotes: builder.query<PartnerQuoteRequest[], void>({
      query: () => ({
        url: endpoints.partners.quotes,
        method: 'GET',
      }),
      providesTags: ['PartnerQuotes'],
    }),

    createPartnerQuote: builder.mutation<PartnerQuoteRequest, CreateQuoteRequestPayload>({
      query: data => ({
        url: endpoints.partners.quotes,
        method: 'POST',
        data,
      }),
      invalidatesTags: ['PartnerQuotes'],
    }),

    getPartnerQuoteById: builder.query<PartnerQuoteRequest, string>({
      query: quoteId => ({
        url: endpoints.partners.quoteById(quoteId),
        method: 'GET',
      }),
      providesTags: (_r, _e, id) => [{ type: 'PartnerQuotes', id }],
    }),

    // ==================== LEADS ====================
    getPartnerLeads: builder.query<PartnerLead[], void>({
      query: () => ({
        url: endpoints.partners.leads,
        method: 'GET',
      }),
      providesTags: ['PartnerLeads'],
    }),

    createPartnerLead: builder.mutation<Lead, CreateLeadDto>({
      query: data => ({
        url: endpoints.partners.leads,
        method: 'POST',
        data,
      }),
      invalidatesTags: ['PartnerLeads'],
    }),

    // ==================== INVOICES ====================
    getPartnerInvoices: builder.query<PartnerInvoice[], void>({
      query: () => ({
        url: endpoints.partners.invoices,
        method: 'GET',
      }),
      providesTags: ['PartnerInvoices'],
    }),

    getPartnerInvoiceById: builder.query<PartnerInvoice, string>({
      query: invoiceId => ({
        url: endpoints.partners.invoiceById(invoiceId),
        method: 'GET',
      }),
      providesTags: (_r, _e, id) => [{ type: 'PartnerInvoices', id }],
    }),

    getPartnerBillingSummary: builder.query<PartnerBillingSummary, void>({
      query: () => ({
        url: endpoints.partners.billingSummary,
        method: 'GET',
      }),
      providesTags: ['PartnerBillingSummary'],
    }),

    // ==================== TEAM MEMBERS ====================
    getPartnerTeamMembers: builder.query<PartnerOwnTeamMember[], void>({
      query: () => ({
        url: endpoints.partners.teamMembers,
        method: 'GET',
      }),
      providesTags: ['PartnerTeamMembers'],
    }),

    addPartnerTeamMember: builder.mutation<PartnerOwnTeamMember, CreateTeamMemberPayload>({
      query: data => ({
        url: endpoints.partners.teamMembers,
        method: 'POST',
        data,
      }),
      invalidatesTags: ['PartnerTeamMembers'],
    }),

    updatePartnerTeamMember: builder.mutation<PartnerOwnTeamMember, { memberId: string; data: UpdateTeamMemberPayload }>({
      query: ({ memberId, data }) => ({
        url: endpoints.partners.teamMemberById(memberId),
        method: 'PUT',
        data,
      }),
      invalidatesTags: ['PartnerTeamMembers'],
    }),

    removePartnerTeamMember: builder.mutation<{ deleted: boolean }, string>({
      query: memberId => ({
        url: endpoints.partners.teamMemberById(memberId),
        method: 'DELETE',
      }),
      invalidatesTags: ['PartnerTeamMembers'],
    }),

    // ==================== CONTACTS ====================
    getPartnerContacts: builder.query<PartnerContacts, void>({
      query: () => ({
        url: endpoints.partners.contacts,
        method: 'GET',
      }),
      providesTags: ['PartnerContacts'],
    }),

    // ==================== MESSAGES ====================
    getPartnerMessages: builder.query<PartnerMessage[], void>({
      query: () => ({
        url: endpoints.partners.messages,
        method: 'GET',
      }),
      providesTags: ['PartnerMessages'],
    }),

    sendPartnerMessage: builder.mutation<PartnerMessage, { message: string }>({
      query: data => ({
        url: endpoints.partners.messages,
        method: 'POST',
        data,
      }),
      invalidatesTags: ['PartnerMessages'],
    }),

    // ==================== ADMIN ENDPOINTS ====================
    getCompanyPartners: builder.query<AdminPartnerRelationship[], string>({
      query: companyId => ({
        url: endpoints.partners.companyPartners(companyId),
        method: 'GET',
      }),
      providesTags: ['AdminPartners'],
    }),

    getProjectPartners: builder.query<AdminPartnerRelationship[], { companyId: string; projectId: string }>({
      query: ({ companyId, projectId }) => ({
        url: endpoints.partners.companyProjectPartners(companyId, projectId),
        method: 'GET',
      }),
      providesTags: (_r, _e, { projectId }) => [{ type: 'AdminPartners', id: projectId }],
    }),

    invitePartner: builder.mutation<InvitePartnerResponse, { companyId: string; data: InvitePartnerPayload }>({
      query: ({ companyId, data }) => ({
        url: endpoints.partners.invitePartner(companyId),
        method: 'POST',
        data,
      }),
      invalidatesTags: ['AdminPartners'],
    }),

    updatePartnerStatus: builder.mutation<AdminPartnerRelationship, { companyId: string; partnerId: string; status: PartnerRelationshipStatus }>({
      query: ({ companyId, partnerId, status }) => ({
        url: endpoints.partners.partnerStatus(companyId, partnerId),
        method: 'PUT',
        data: { status },
      }),
      invalidatesTags: ['AdminPartners'],
    }),

    reinvitePartner: builder.mutation<InvitePartnerResponse, { companyId: string; partnerId: string }>({
      query: ({ companyId, partnerId }) => ({
        url: endpoints.partners.reinvitePartner(companyId, partnerId),
        method: 'POST',
      }),
      invalidatesTags: ['AdminPartners'],
    }),

    assignProjectToPartner: builder.mutation<AdminPartnerRelationship, { companyId: string; partnerId: string; projectId: string }>({
      query: ({ companyId, partnerId, projectId }) => ({
        url: endpoints.partners.partnerProjects(companyId, partnerId),
        method: 'POST',
        data: { projectId },
      }),
      invalidatesTags: (_r, _e, { projectId }) => ['AdminPartners', { type: 'AdminPartners', id: projectId }],
    }),

    unassignProjectFromPartner: builder.mutation<AdminPartnerRelationship, { companyId: string; partnerId: string; projectId: string }>({
      query: ({ companyId, partnerId, projectId }) => ({
        url: endpoints.partners.unassignProject(companyId, partnerId, projectId),
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, { projectId }) => ['AdminPartners', { type: 'AdminPartners', id: projectId }],
    }),

    getAllPartnerQuotesAdmin: builder.query<(PartnerQuoteRequest & { partner?: { companyName: string; email: string; contactName?: string } })[], string>({
      query: companyId => ({
        url: endpoints.partners.allQuotesAdmin(companyId),
        method: 'GET',
      }),
      providesTags: ['AdminPartnerQuotes'],
    }),

    getPartnerQuotesAdmin: builder.query<PartnerQuoteRequest[], { companyId: string; partnerId: string }>({
      query: ({ companyId, partnerId }) => ({
        url: endpoints.partners.partnerQuotesAdmin(companyId, partnerId),
        method: 'GET',
      }),
      providesTags: ['AdminPartnerQuotes'],
    }),

    updateQuoteStatusAdmin: builder.mutation<PartnerQuoteRequest, { companyId: string; quoteId: string; status: string; estimatedValue?: number }>({
      query: ({ companyId, quoteId, status, estimatedValue }) => ({
        url: endpoints.partners.quoteStatusAdmin(companyId, quoteId),
        method: 'PUT',
        data: { status, estimatedValue },
      }),
      invalidatesTags: ['AdminPartnerQuotes'],
    }),
  }),
});

export const {
  usePartnerLoginMutation,
  useAcceptPartnerInviteMutation,
  useGetPartnerProfileQuery,
  useUpdatePartnerProfileMutation,
  useGetPartnerCompaniesQuery,
  useSwitchPartnerCompanyMutation,
  useGetPartnerProjectsQuery,
  useGetPartnerProjectByIdQuery,
  useGetPartnerProjectChatMessagesQuery,
  useGetPartnerQuotesQuery,
  useCreatePartnerQuoteMutation,
  useGetPartnerQuoteByIdQuery,
  useGetPartnerLeadsQuery,
  useCreatePartnerLeadMutation,
  useGetPartnerInvoicesQuery,
  useGetPartnerInvoiceByIdQuery,
  useGetPartnerBillingSummaryQuery,
  useGetPartnerTeamMembersQuery,
  useAddPartnerTeamMemberMutation,
  useUpdatePartnerTeamMemberMutation,
  useRemovePartnerTeamMemberMutation,
  useGetPartnerContactsQuery,
  useGetPartnerMessagesQuery,
  useSendPartnerMessageMutation,
  // Admin hooks
  useGetCompanyPartnersQuery,
  useGetProjectPartnersQuery,
  useInvitePartnerMutation,
  useUpdatePartnerStatusMutation,
  useReinvitePartnerMutation,
  useAssignProjectToPartnerMutation,
  useUnassignProjectFromPartnerMutation,
  useGetAllPartnerQuotesAdminQuery,
  useGetPartnerQuotesAdminQuery,
  useUpdateQuoteStatusAdminMutation,
} = partnersApi;
